<template>
  <section>
    <page-header
      icon="mdi-email-newsletter"
      :title="$t('post_report')"
      :subtitle="$t('manage_post_reports')"
    >
      <template #actions>
        <print-button @click="handlePrint" />
      </template>
    </page-header>

    <page-toolbar
      v-model="searchQuery"
      :search-placeholder="$t('search_post_reports_placeholder')"
    >
      <template #filters>
        <v-col cols="auto">
          <date-picker v-model="selectedDateRange" color="white" />
        </v-col>

        <v-col cols="auto">
          <autocomplete
            v-model="selectedSendType"
            :items="sendTypeOptions"
            @change="loadReport"
            :label="$t('view')"
            item-text="name"
            item-value="id"
            clearable
            chips
            small-chips
            color="white"
          />
        </v-col>
      </template>
    </page-toolbar>

    <div style="width: 95%; margin: 10px auto">
      <v-data-table
        :headers="tableHeaders"
        :items="reportData"
        :search="searchQuery"
        :loading="isLoadingReport"
        :loading-text="$t('loading_data')"
        class="ordera-box-shadow striped-table"
        dense
        hide-default-footer
        disable-pagination
      >
        <template v-slot:top>
          <v-divider class="mt-2"></v-divider>
        </template>

        <!-- Table Cell Templates -->
        <template v-slot:item.id="{ item }">
          <span v-if="isTotalRow(item)">{{ $t("totalU") }}</span>
          <span v-else class="clickable-id" @click="showPackageUsers(item)">
            {{ item.id }}
          </span>
        </template>

        <template v-slot:item.num="{ item }">
          #{{ isTotalRow(item) ? "" : item.id }}
        </template>

        <template v-slot:item.send_type="{ item }">
          {{ formatSendType(item.send_type) }}
        </template>

        <template v-slot:item.post="{ item }">
          {{ item.post?.name || "" }}
        </template>

        <template v-slot:item.sender="{ item }">
          {{ formatFullName(item.sender) }}
        </template>

        <template v-slot:item.receiver="{ item }">
          {{ formatFullName(item.receiver) }}
        </template>

        <template v-slot:item.send_date="{ item }">
          <span v-if="isSingleDayRange && !isTotalRow(item)">
            {{ formatDateTime(item.date) }}
          </span>
        </template>

        <template v-slot:item.action="{ item }">
          <a
            v-if="!isTotalRow(item)"
            :title="$t('months_report')"
            href="#"
            @click.prevent="showPackageDetail(item)"
          >
            <v-icon style="font-size: 17px" color="primary">
              mdi-format-list-bulleted
            </v-icon>
          </a>
        </template>
      </v-data-table>
    </div>

    <!-- Package Detail Dialog -->
    <post-package-detail-dialog
      v-model="isDetailDialogOpen"
      :package-detail="selectedPackageDetail"
    />
  </section>
</template>

<script>
import moment from "moment";
import hasSnackbar from "@/mixins/hasSnackbar";
import postService from "@/services/post";
import PostPackageDetailDialog from "@/components/postReport/PostPackageDetailDialog.vue";

export default {
  mixins: [hasSnackbar],
  name: "PostReport",
  components: {
    PostPackageDetailDialog,
  },
  data() {
    return {
      // Loading states
      isLoadingReport: false,

      // Date & Filter
      selectedDateRange: [new Date().toISOString().substr(0, 10)],
      selectedSendType: 1,
      searchQuery: "",

      // Report data
      reportData: [],
      originalReportData: [],

      // Dialog states
      isDetailDialogOpen: false,
      selectedPackageDetail: null,
    };
  },
  computed: {
    /**
     * Table headers configuration
     */
    tableHeaders() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("delivery_post"), value: "post" },
        { text: this.$t("sender"), value: "sender" },
        { text: this.$t("receiver"), value: "receiver" },
        { text: this.$t("type"), value: "send_type" },
        { text: this.$t("date"), value: "send_date" },
        { text: this.$t("details"), value: "action", sortable: false },
      ];
    },

    /**
     * Send type filter options
     */
    sendTypeOptions() {
      return [
        { name: this.$t("local"), id: 1 },
        { name: this.$t("intercity"), id: 2 },
      ];
    },

    /**
     * Check if selected date range is a single day
     */
    isSingleDayRange() {
      return (
        this.selectedDateRange &&
        this.selectedDateRange.length === 2 &&
        this.selectedDateRange[0] === this.selectedDateRange[1]
      );
    },

    /**
     * Current user from store
     */
    currentUser() {
      return this.$store.state.user?.user;
    },

    /**
     * Current restaurant from store
     */
    currentRestaurant() {
      return this.$store.state.restaurant;
    },
  },
  watch: {
    /**
     * Watch for date range changes and reload report
     */
    selectedDateRange: {
      handler() {
        this.loadReport();
      },
      deep: true,
    },

    /**
     * Watch for locale changes
     */
    "$i18n.locale"() {
      this.$forceUpdate();
    },
  },
  created() {
    this.loadReport();
  },
  methods: {
    /**
     * Load post report data from API
     */
    async loadReport() {
      if (!this.currentRestaurant) {
        return;
      }

      // Normalize date range
      if (this.selectedDateRange && this.selectedDateRange.length === 1) {
        this.selectedDateRange.push(this.selectedDateRange[0]);
      }

      // Sort dates if needed
      if (
        this.selectedDateRange &&
        this.selectedDateRange[0] > this.selectedDateRange[1]
      ) {
        [this.selectedDateRange[0], this.selectedDateRange[1]] = [
          this.selectedDateRange[1],
          this.selectedDateRange[0],
        ];
      }

      this.isLoadingReport = true;
      try {
        const { data } = await postService.getPostReports({
          date: moment(this.selectedDateRange[0]).format("YYYY-MM-DD"),
          date2: moment(this.selectedDateRange[1]).format("YYYY-MM-DD"),
          sendType: this.selectedSendType,
        });

        this.reportData = data || [];
        this.originalReportData = data || [];
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
        this.reportData = [];
      } finally {
        this.isLoadingReport = false;
      }
    },

    /**
     * Show package detail dialog
     */
    showPackageDetail(packageItem) {
      this.selectedPackageDetail = packageItem;
      this.isDetailDialogOpen = true;
    },

    /**
     * Show users for a package (placeholder for future implementation)
     */
    showPackageUsers(packageItem) {
      // TODO: Implement user list dialog
      console.log("Show users for package:", packageItem);
    },

    /**
     * Handle print action
     */
    handlePrint() {
      window.print();
    },

    /**
     * Check if row is the total row
     */
    isTotalRow(item) {
      return item.id === this.$t("totalU");
    },

    /**
     * Format send type to localized string
     */
    formatSendType(sendType) {
      return sendType === 1 ? this.$t("local") : this.$t("intercity");
    },

    /**
     * Format full name from user object
     */
    formatFullName(user) {
      if (!user) return "";
      return `${user.name || ""} ${user.lastname || ""}`.trim();
    },

    /**
     * Format date and time
     */
    formatDateTime(date) {
      return date ? moment(date).format("MM.DD. HH:mm") : "";
    },
  },
};
</script>

<style scoped>
.clickable-id {
  cursor: pointer;
  color: #1976d2;
  text-decoration: underline;
}

.clickable-id:hover {
  color: #1565c0;
}
</style>
